/*
 * Processvisualisering med 9 steg
 * Baserad på Christoffers material: "Så går det till – Från idé till färdigt uterum"
 * Marknadsplan: Pelare 3 "En process att lita på"
 * Kundresa Fas 3: Trygghet
 */
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/ScrollAnimations";
import {
  Home,
  PenTool,
  FileCheck,
  Ruler,
  Factory,
  Shovel,
  Wrench,
  ClipboardCheck,
  PartyPopper,
} from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Hembesök",
    description: "Vi kommer hem till dig för behovsanalys, mättagning och rådgivning. Helt kostnadsfritt och kravlöst.",
    detail: "LiDAR-uppmätning med millimeterprecision",
    icon: Home,
  },
  {
    number: "02",
    title: "Design och offert",
    description: "3D-visualisering av ditt framtida uterum. Val av paket och material. Fast pris utan överraskningar.",
    detail: "Skräddarsydd 3D-rendering",
    icon: PenTool,
  },
  {
    number: "03",
    title: "Bygglov",
    description: "Vi hanterar ritningar, ansökan och dialog med kommunen. Bygglovet ingår i vårt uppdrag.",
    detail: "Komplett bygglovshantering",
    icon: FileCheck,
  },
  {
    number: "04",
    title: "Projektering",
    description: "Konstruktion, energiberäkning vid behov, materialval och detaljlösningar utarbetas av våra ingenjörer.",
    detail: "Teknisk konstruktionsdokumentation",
    icon: Ruler,
  },
  {
    number: "05",
    title: "Produktion",
    description: "Tillverkning i egen fabrik i Eskilstuna. Stomme, glas och aluminiumprofiler med kvalitetskontroll i varje steg.",
    detail: "Svensktillverkat med patenterade lösningar",
    icon: Factory,
  },
  {
    number: "06",
    title: "Mark och grund",
    description: "Schakt och förberedelser. Platta på mark eller plintgrund. Förberett för el och VVS.",
    detail: "Allt markarbete ingår",
    icon: Shovel,
  },
  {
    number: "07",
    title: "Montage",
    description: "Stomme och tak monteras. Glaspartier installeras. El, VVS och invändiga arbeten slutförs av våra egna hantverkare.",
    detail: "Egna montörer genom hela bygget",
    icon: Wrench,
  },
  {
    number: "08",
    title: "Slutbesiktning",
    description: "Genomgång och kontroll av hela projektet. Justering vid behov. Formellt godkännande.",
    detail: "Oberoende besiktning och garanti",
    icon: ClipboardCheck,
  },
  {
    number: "09",
    title: "Färdigt resultat",
    description: "Inflyttningsklart. Fullvärdigt uterum för hela familjen, året runt. Njut av ditt nya rum.",
    detail: "Garanti och eftermarknadssupport",
    icon: PartyPopper,
  },
];

interface ProcessStepsProps {
  variant?: "default" | "light";
  compact?: boolean;
}

export default function ProcessSteps({ variant = "default", compact = false }: ProcessStepsProps) {
  const isDark = variant === "default";
  const displaySteps = compact ? [steps[0], steps[1], steps[4], steps[6], steps[8]] : steps;

  return (
    <section className={isDark ? "py-12 sm:py-16 lg:py-24 bg-navy" : "py-12 sm:py-16 lg:py-24 bg-white"}>
      <div className="container">
        <FadeUp>
          <div className="max-w-2xl mb-10 sm:mb-14 lg:mb-16">
            <p className={`font-heading font-bold text-xs sm:text-sm tracking-widest uppercase mb-3 ${isDark ? "text-copper-light" : "text-copper"}`}>
              En process att lita på
            </p>
            <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 ${isDark ? "text-white" : "text-navy"}`}>
              {compact ? "Från din dröm till verklighet" : "Så går det till – från idé till färdigt uterum"}
            </h2>
            <p className={`text-base sm:text-lg leading-relaxed ${isDark ? "text-white/70" : "text-muted-foreground"}`}>
              {compact
                ? "Vi gör resan enkel och trygg. Varje steg är utformat för att du ska känna dig sedd och omhändertagen."
                : "Vi tar hand om hela resan. Från första hembesöket till inflyttningsklart resultat – en kontakt, en leverantör, en garanti."}
            </p>
          </div>
        </FadeUp>

        <StaggerContainer
          className={compact
            ? "grid sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8"
            : "grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          }
          staggerDelay={0.08}
        >
          {displaySteps.map((step) => {
            const Icon = step.icon;
            return (
              <StaggerItem key={step.number}>
                <div className="relative group p-4 -m-4 rounded-xl transition-all duration-250 hover:bg-white/5">
                  {/* Large decorative number */}
                  <div
                    aria-hidden="true"
                    className={`font-heading font-bold text-5xl sm:text-6xl lg:text-7xl leading-none mb-3 ${
                      isDark ? "text-white/40" : "text-navy/40"
                    }`}
                  >
                    {step.number}
                  </div>
                  {/* Icon + accent line */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? "bg-white/10" : "bg-navy/5"}`}>
                      <Icon className={`w-5 h-5 ${isDark ? "text-copper-light" : "text-copper"}`} />
                    </div>
                    <div className={`flex-1 h-0.5 ${isDark ? "bg-rose" : "bg-rose"}`} />
                  </div>
                  <h3
                    className={`font-heading text-lg sm:text-xl font-bold mb-2 sm:mb-3 ${
                      isDark ? "text-white" : "text-navy"
                    }`}
                  >
                    {step.title}
                  </h3>
                  <p
                    className={`text-sm sm:text-base leading-relaxed mb-3 ${
                      isDark ? "text-white/70" : "text-muted-foreground"
                    }`}
                  >
                    {step.description}
                  </p>
                  <p
                    className={`text-xs sm:text-sm font-medium ${
                      isDark ? "text-copper-light" : "text-copper"
                    }`}
                  >
                    {step.detail}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>

        {/* Idé till Klart progression bar */}
        {!compact && (
          <FadeUp delay={0.3}>
            <div className="mt-12 sm:mt-16">
              <div className="flex items-center justify-between max-w-3xl mx-auto">
                {["Idé", "Ritning", "Bygglov", "Produktion", "Bygg", "Klart"].map((label, i, arr) => (
                  <div key={label} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${isDark ? "bg-copper-light" : "bg-copper"}`} />
                      <span className={`text-xs mt-2 font-medium ${isDark ? "text-white/60" : "text-muted-foreground"}`}>
                        {label}
                      </span>
                    </div>
                    {i < arr.length - 1 && (
                      <div className={`w-8 sm:w-12 md:w-16 lg:w-20 h-0.5 mx-1 ${isDark ? "bg-white/15" : "bg-navy/10"}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        )}
      </div>
    </section>
  );
}
